import { container } from '@graphistry/falcor-react-redux';
import styles from '@graphistry/viz/src/components/histograms/styles.less';
import { ExpressionTemplates } from '@graphistry/viz/src/components/expressions';
import { Sparkline, SparklineBar } from '@graphistry/viz/src/components/histograms';

import {
    binTouchMove,
    binTouchStart,
    binTouchCancel,
    yScaleChanged,
    addHistogram,
    clearHighlight,
    removeHistogram
} from '@graphistry/viz/src/actions/histograms';

import { setEncoding } from '@graphistry/viz/src/actions/encodings';

import { createLogger } from '@graphistry/common/logger';
const logger = createLogger(__filename);

let Histograms = ({
    addHistogram,
    removeHistogram,
    setEncoding,
    encodings,
    templates = [],
    histograms = [],
    loading = false,
    className = '',
    style = {},
    legend,
    children,
    visible,
    ...props
}) => {
    let showLoadingInHeader = false;

    const histogramItems = Array.from(histograms, (histogram, index) => {
        let style;
        if (!histogram) {
            showLoadingInHeader = loading;
            style = { borderBottomStyle: 'dashed' };
            histogram = { id: 'pending', componentType: '' };
        }
        return (
            <Histogram
                style={style}
                data={histogram}
                setEncoding={setEncoding}
                encodings={{ ...encodings }}
                key={`${index}: ${histogram.id}`}
                removeHistogram={removeHistogram}
            />
        );
    });

    return (
        <div style={style} className={className + ' ' + styles['flex-vertical']}>
            {children && children[0]}
            {(visible && (
                <React.Fragment>
                    <div>
                        <ExpressionTemplates
                            showDataTypes={false}
                            menuPlacement="top"
                            addExpression={addHistogram}
                            loading={showLoadingInHeader}
                            placeholder="Add histogram for..."
                            templates={templates.filter(({ isInternal }) => !isInternal)}
                        />
                    </div>
                    <div className={styles['histograms-list']}>
                        {histogramItems}
                    </div>
                </React.Fragment>
            )) ||
                undefined}
            {children && children.slice(1)}
        </div>
    );
};

const withHistogramsListContainer = container({
    renderLoading: true,
    fragment: ({ templates = [], encodings, ...histograms } = {}) => {
        return `{
            templates: {
                length, [0...${templates.length}]: {
                    name, isInternal, dataType, identifier, componentType
                }
            },
            id, name, visible, length, ...${Histogram.fragments(histograms)},
            encodings: {
                options: {
                    ['point', 'edge']: { color }
                },
                point: { color, size, icon },
                edge: { color, icon }
            }
        }`;
    },
    mapFragment: histograms => ({
        histograms,
        id: histograms.id,
        name: histograms.name,
        visible: histograms.visible,
        templates: histograms.templates,
        encodings: histograms.encodings
    }),
    dispatchers: {
        addHistogram,
        removeHistogram,
        setEncoding
    }
});

Histograms = withHistogramsListContainer(Histograms);

let Histogram = ({
    range = [],
    loading = false,
    dataType,
    componentType,
    id,
    name,
    height,
    yScale = 'none',
    encodings,
    filter,
    global: _global = {},
    masked = {},
    filtered = {},
    barTooltipPlacement = 'top',
    removeHistogram,
    yScaleChanged,
    setEncoding,
    clearHighlight,
    binTouchMove,
    binTouchStart,
    binTouchCancel
}) => {
    const { [componentType]: encodingDescription = {} } = encodings || {};
    const {
        color: { legend: colors = [], attribute: encodedAttribute } = {}
    } = encodingDescription;

    range = (filter && range) || [];
    const trans = Math[yScale] || (x => x);
    const enabled = !filter || filter.enabled;
    const isFiltered = range && range.length > 0;
    const isEncoded = encodings && name === encodedAttribute;
    const { bins: filteredBins = [] } = filtered;
    const { bins: maskedBins = [], isMasked } = masked;
    const { bins: globalBins = [], numBins = 1, max = 1, binType = 'nodata' } = _global;

    return (
        <Sparkline
            id={id}
            name={name}
            height={height}
            yScale={yScale}
            loading={loading}
            filtered={isFiltered}
            encodings={encodings}
            onClose={removeHistogram}
            setEncoding={setEncoding}
            isFilterEnabled={enabled}
            componentType={componentType}
            className={styles['histogram']}
            onYScaleChanged={value => yScaleChanged({ key: 'yScale', value })}
            onClearHighlight={event => clearHighlight({ id, event, componentType })}>
            {globalBins.map(
                (
                    { values, count: globalCount },
                    binID,
                    bins,
                    binIsFiltered = isFiltered &&
                        !!(
                            (binID >= range[0] && binID <= range[range.length - 1]) ||
                            ~range.indexOf(binID)
                        ),
                    { count: maskedCount = 0 } = (maskedBins[binID] || {}),
                    { count: filteredCount = 0 } = (filteredBins[binID] || {})
                ) => (
                    <SparklineBar
                        index={binID}
                        tooltipPlacement={barTooltipPlacement}
                        filtered={binIsFiltered}
                        key={`${id}-bar-${binID}`}
                        name={name}
                        values={values}
                        componentType={componentType}
                        color={isEncoded && colors[binID]}
                        binWidth={`${100 * (1 / (numBins || 1))}%`}
                        filterBounds={{
                            leftest: binIsFiltered && binID <= range[0],
                            rightest: binIsFiltered && binID >= range[range.length - 1]
                        }}
                        globalCount={globalCount}
                        maskedCount={maskedCount}
                        filteredCount={filteredCount}
                        binType={binType}
                        dataType={dataType}
                        isMasked={isMasked}
                        globalHeight={`${100 * (trans(globalCount || 0) / (trans(max) || 1))}%`}
                        maskedHeight={`${100 * (trans(maskedCount || 0) / (trans(max) || 1))}%`}
                        filteredHeight={`${100 * (trans(filteredCount || 0) / (trans(max) || 1))}%`}
                        onBinTouchMove={event => !loading && binTouchMove({ event, binID, range, binIsFiltered })}
                        onBinTouchStart={event => !loading && binTouchStart({ event, binID, range, binIsFiltered })}
                        onBinTouchCancel={event => !loading && binTouchCancel({ event, binID, range, binIsFiltered })}
                    />
                )
            )}
        </Sparkline>
    );
};

const withHistogramContainer = container({
    renderLoading: true,
    fragment: ({ id, name, global, masked, filtered } = {}) => {
        return `{
            filter: { enabled },
            id, name, range, yScale, dataType, componentType,
            global: ${GlobalHistogramBins.fragment(global)}${global ? `,
            masked: ${MaskedHistogramBins.fragment(masked)},
            filtered: ${MaskedHistogramBins.fragment(filtered)}` : ``}
        }`;
    },
    dispatchers: {
        binTouchMove,
        binTouchStart,
        binTouchCancel,
        clearHighlight,
        yScaleChanged
    }
});

Histogram = withHistogramContainer(Histogram);

let withGlobalHistogramBinsContainer = container({
    renderLoading: true,
    fragment: ({ bins = [] } = {}) => !(bins && bins.length) ?
        `{ bins: { length } }` :
        `{ bins: ${HistogramBin.fragments(bins)}, binType, numBins, min, max }`
});

let withMaskedHistogramBinsContainer = container({
    renderLoading: true,
    fragment: ({ bins = [] } = {}) => !(bins && bins.length) ?
        `{ bins: { length } }` :
        `{ bins: ${HistogramBin.fragments(bins)}, isMasked }`
});

let withHistogramBinContainer = container({
    renderLoading: true,
    fragment: () => `{
        count, values, exclude
    }`
});

export let GlobalHistogramBins = withGlobalHistogramBinsContainer(() => {});
export let MaskedHistogramBins = withMaskedHistogramBinsContainer(() => {});
export let HistogramBin = withHistogramBinContainer(() => {});

export { Histograms, Histogram };
export {
    withHistogramsListContainer,
    withHistogramContainer,
    withGlobalHistogramBinsContainer,
    withMaskedHistogramBinsContainer,
    withHistogramBinContainer
};
